import React, { useMemo } from "react";
import { FiMapPin } from "react-icons/fi";

import TrendSparkline from "../ReusableComponents/TrendSparkline";
import { Skeleton, SkeletonText } from "../Skeleton";
import { useInViewLoad } from "../../hooks/useInViewLoad";
import {
  cardSources,
  dashboardSources,
  describeLinkedAssets,
  formatSourceType,
  forecastHorizonDays,
  forecastSeriesKey,
  getForecastSeries,
} from "../../utils/composedForecastUtils";
import type { ComposedForecast } from "../../types/composedForecast";

interface ComposedForecastCardProps {
  forecast: ComposedForecast;
  onOpen: (forecast: ComposedForecast) => void;
  onEdit: (forecast: ComposedForecast) => void;
  /** Admin or owner; org members read only, so they get no Edit */
  canManage: boolean;
  /** Admin-only for now */
  onCopy?: (forecast: ComposedForecast) => void;
}

// The view at a glance: its trend and what it's built from; readings and the edit time live in the detail modal
const ComposedForecastCard: React.FC<ComposedForecastCardProps> = ({ forecast, onOpen, onEdit, canManage, onCopy }) => {
  const sourceCount = dashboardSources(forecast).length;
  const openable = sourceCount > 0;
  const shownSources = cardSources(forecast);
  const hiddenCount = sourceCount - shownSources.length;
  // Truncates on a narrow card, so the title keeps a long method name reachable
  const { ref, loaded, data: series } = useInViewLoad(forecastSeriesKey(forecast), () => getForecastSeries(forecast));
  // Every source with data gets a line in the colour the full chart draws it in; the first one leads the time window
  const lines = useMemo(
    () =>
      (series?.sources || [])
        .filter((src) => src.points.length > 1)
        .map((src) => ({ points: src.points, color: src.color })),
    [series]
  );

  return (
    // The whole card opens the custom view; inner controls stop propagation so managing never also opens
    <div
      className={`flex h-full w-full min-w-0 flex-col gap-3 p-4 ${openable ? "eai-card-interactive cursor-pointer" : "eai-card"}`}
      role={openable ? "button" : undefined}
      tabIndex={openable ? 0 : undefined}
      aria-label={openable ? `View ${forecast.name}` : undefined}
      title={openable ? undefined : "This custom view has no data sources yet"}
      onClick={openable ? () => onOpen(forecast) : undefined}
      onKeyDown={
        openable
          ? (e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen(forecast);
              }
            }
          : undefined
      }
    >
      <div className="flex items-start gap-2.5">
        <div className="min-w-0 flex-1">
          <h3 className="eai-card-title line-clamp-2 min-h-[2.4em] break-words" title={forecast.name}>
            {forecast.name}
          </h3>
          {/* Clamped to two lines so a long description can't stretch the row; the full text is the tooltip */}
          {forecast.description && (
            <p className="eai-card-description mt-1" title={forecast.description}>
              {forecast.description}
            </p>
          )}
        </div>
        {/* Active is a real state worth colouring; inactive is just a fact, so it stays neutral */}
        <span
          className={`eai-tag-pill-sm mt-px shrink-0 whitespace-nowrap ${
            forecast.isActive ? "border-eai-success-border bg-eai-success-surface-soft text-eai-success-text" : ""
          }`}
        >
          {forecast.isActive ? "Active" : "Inactive"}
        </span>
      </div>

      {/* One panel holds the trend and a one-line legend naming each kind of source — no readings, the card is only a glance */}
      <div ref={ref} className="overflow-hidden rounded-md border border-eai-border">
        <div className="px-3 pb-2 pt-3">
          {!loaded ? (
            <Skeleton className="h-16 w-full" radius="md" />
          ) : lines.length > 0 ? (
            <TrendSparkline lines={lines} horizonDays={forecastHorizonDays(series) ?? undefined} className="h-16 w-full text-eai-ink-faint" />
          ) : (
            <div className="flex h-16 items-center justify-center font-inter text-eai-body-xs text-eai-ink-faint">No data yet</div>
          )}
        </div>
        <div className="flex min-h-8 flex-wrap items-center gap-x-3 gap-y-1 border-t border-eai-border px-3 py-1.5 font-inter text-eai-body-xs">
          {!loaded ? (
            <Skeleton className="h-4 w-2/3" />
          ) : (
            <>
              {/* One entry per source, by type; its name is the hover title, and one with no data yet fades since it has no line */}
              {(series?.sources || []).map((source) => {
                const hasData = source.points.length > 1;
                return (
                  <span
                    key={source.id}
                    className={`flex items-center gap-1.5 whitespace-nowrap ${hasData ? "text-eai-ink-subtle" : "text-eai-ink-faint"}`}
                    title={hasData ? source.name : `${source.name} · No data yet`}
                  >
                    <span
                      aria-hidden
                      className={`h-2 w-2 shrink-0 rounded-full ${hasData ? "" : "opacity-40"}`}
                      style={{ backgroundColor: source.color }}
                    />
                    {formatSourceType(source.sourceType)}
                  </span>
                );
              })}
              {hiddenCount > 0 && <span className="whitespace-nowrap text-eai-ink-faint">+{hiddenCount} more</span>}
            </>
          )}
        </div>
      </div>


      <div className="mt-auto flex items-center gap-2">
        <FiMapPin aria-hidden className="h-3.5 w-3.5 shrink-0 text-eai-ink-subtle" />
        <span className="min-w-0 flex-1 truncate font-inter text-eai-body-xs text-eai-ink-subtle">
          {describeLinkedAssets(forecast)}
        </span>
        {onCopy && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onCopy(forecast);
            }}
            className="eai-btn-outline-xs"
            aria-label={`Duplicate ${forecast.name}`}
          >
            Duplicate
          </button>
        )}
        {canManage && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(forecast);
            }}
            className="eai-btn-outline-xs"
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
};

// Placeholder matching ComposedForecastCard's layout — keep the two in sync when the card changes.
export const ComposedForecastCardSkeleton = () => (
  <div className="eai-card flex h-full w-full min-w-0 flex-col gap-3 p-4">
    <div className="flex items-start gap-2.5">
      <div className="flex-1">
        <Skeleton className="h-3.5 w-2/3" />
        <Skeleton className="mt-2 h-3 w-1/3" />
      </div>
      <Skeleton className="h-5 w-14 shrink-0" radius="full" />
    </div>
    <div className="overflow-hidden rounded-md border border-eai-border">
      <div className="px-3 pb-2 pt-3">
        <Skeleton className="h-16 w-full" radius="md" />
      </div>
      <div className="flex min-h-8 items-center border-t border-eai-border px-3 py-1.5">
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
    <SkeletonText lines={2} lineClassName="h-3" />
    <div className="mt-auto flex items-center gap-2">
      <Skeleton className="h-3 w-28" />
      <Skeleton className="ml-auto h-eai-control-xs w-12" radius="full" />
    </div>
  </div>
);

export default ComposedForecastCard;
